import { defineStore } from 'pinia';
import { axios } from '@boot/axios.js';

/**
 * Products store for managing product-related data.
 */
export const useProductsStore = defineStore('products', {
    state: () => ({
        products: {},
        productLinks: {}, // to store product links
        productCategories: [],
        productProperties: []
    }),

    getters: {
        /**
         * Get product by id.
         * @param {number} id - The product id to retrieve.
         * @returns {Object|null} The product object if found, null otherwise.
         */
        getProduct: state => id => {
            if (id in state.products) {
                return state.products[id];
            } else {
                return null;
            }
        },

        /**
         * Get product link by id.
         * @param {Object} params - Parameters for finding product link.
         * @param {number} params.assetId - The asset id.
         * @param {number} params.productId - The product id.
         * @returns {Object|null} The product link if found, null otherwise.
         */
        getProductLink:
            state =>
            ({ assetId, productId }) => {
                const productLink = state.productLinks[assetId];
                if (
                    productLink &&
                    productLink.productId.product === productId
                ) {
                    return productLink.productId;
                } else if (productLink && productLink[productId]) {
                    return productLink[productId];
                }

                return null;
            },

        /**
         * Get Product Categories from the store.
         * @returns {Array} - Product Categories
         */
        getProductCategories: state => state.productCategories,

        /**
         * Get product category by id.
         * @param {Object} state
         * @param {number} id
         * @returns {Object} - Product Category
         */
        getProductCategory: state => id => {
            const productCategory = state.productCategories.find(
                category => category.id === id
            );
            return productCategory ? productCategory : null;
        },

        /**
         * Get product properties from the store.
         * @returns {Array} - Product Properties
         */
        getProductProperties: state => state.productProperties
    },

    actions: {
        /**
         * Resets the state to its default values.
         */
        resetState() {
            this.$reset();
        },

        /**
         * Load product from server.
         * @param {number} productId - The id of the product to load.
         * @returns {Promise} Promise object representing the API request.
         */
        loadProductAction(productId) {
            return new Promise((resolve, reject) => {
                axios
                    .get(`products/${productId}/`)
                    .then(response => {
                        this.setProduct(this.formatProduct(response.data));
                        resolve(response);
                    })
                    .catch(error => {
                        console.error('Failed to load product due to: ', error);
                        reject(error);
                    });
            });
        },

        /**
         * Load products for a given facility from the API.
         * @param {number} facilityId - The facility id to load products for.
         * @returns {Promise} Promise object representing the API request.
         */
        loadProductsAction(facilityId) {
            return new Promise((resolve, reject) => {
                axios
                    .get(`products/?facility=${facilityId}`)
                    .then(response => {
                        response.data.results.forEach(product =>
                            this.setProduct(this.formatProduct(product))
                        );
                        resolve(response);
                    })
                    .catch(error => {
                        console.error(
                            'Failed to load products due to: ',
                            error
                        );
                        reject(error);
                    });
            });
        },

        /**
         * Set product in store.
         * @param {Object} product - The product to save.
         */
        setProduct(product) {
            this.products[product.id] = { ...product };
        },

        /**
         * Load product from server.
         * @param {Object} params - Parameters for loading product link.
         * @param {number} params.facilityId - The facility id.
         * @param {number} params.assetId - The asset id.
         * @param {number} params.productId - The product id.
         * @returns {Promise} Promise object representing the API request.
         */
        loadProductLinkAction({ facilityId, assetId, productId }) {
            return new Promise((resolve, reject) => {
                axios
                    .get(`assets/${assetId}/products/?product_id=${productId}`)
                    .then(response => {
                        let productLink = response.data;
                        productLink.facility = facilityId;
                        this.setProductLink({
                            assetId,
                            productLink
                        });
                        this.setProduct(this.formatProduct(productLink));
                        resolve(response);
                    })
                    .catch(error => {
                        console.error(
                            'Failed to load product link due to: ',
                            error
                        );
                        reject(error);
                    });
            });
        },

        /**
         * Set product link in store.
         * @param {Object} params - Parameters for setting product link.
         * @param {number} params.assetId - The asset id.
         * @param {Object} params.productLink - The product link object to save.
         */
        setProductLink({ assetId, productLink }) {
            const productId = productLink.product;
            if (assetId in this.productLinks) {
                this.productLinks[assetId][productId] = { ...productLink };
            } else {
                this.productLinks[assetId] = { productId: { ...productLink } };
            }
        },

        /**
         * Load product categories from API.
         * @returns {Promise} Promise object representing the API request.
         */
        loadProductCategoriesAction() {
            return new Promise((resolve, reject) => {
                axios
                    .get(`/product_categories/`)
                    .then(response => {
                        let productCategories = response.data.results.map(
                            category => ({
                                id: category.id,
                                name: category.name
                            })
                        );
                        this.setProductCategories(productCategories);
                        resolve(response);
                    })
                    .catch(error => {
                        console.error(
                            'Failed to load product categories due to: ',
                            error
                        );
                        reject(error);
                    });
            });
        },

        /**
         * Set product categories in store.
         * @param {Array} productCategories - The product categories to set.
         */
        setProductCategories(productCategories) {
            this.productCategories = [...productCategories];
        },

        /**
         * Load product properties from API.
         * @returns {Promise} Promise object representing the API request.
         */
        loadProductPropertiesAction() {
            return new Promise((resolve, reject) => {
                axios
                    .get(`/product_properties/`)
                    .then(response => {
                        const productProperties = response.data.results.map(
                            property => ({
                                id: property.id,
                                name: property.name,
                                product_category: property.product_category,
                                allowed_values: property.allowed_values
                            })
                        );
                        this.setProductProperties(productProperties);
                        resolve(response);
                    })
                    .catch(error => {
                        console.error(
                            'Failed to load product properties due to: ',
                            error
                        );
                        reject(error);
                    });
            });
        },

        /**
         * Set product properties in store.
         * @param {Array} productProperties - The product properties to set.
         */
        setProductProperties(productProperties) {
            this.productProperties = [...productProperties];
        },

        /**
         * Send a request to create a product in the backend API.
         * @param {Object} category - The product category to create.
         * @returns {Promise} Promise object representing the API request.
         */
        createProductCategoryAction(category) {
            return new Promise((resolve, reject) => {
                axios
                    .post(`/product_categories/`, category)
                    .then(response => {
                        let productCategory = {
                            id: response.data.id,
                            name: response.data.name
                        };
                        this.addProductCategory(productCategory);
                        resolve(response);
                    })
                    .catch(error => {
                        console.error(
                            'Failed to create product category due to: ',
                            error
                        );
                        reject(error);
                    });
            });
        },

        /**
         * Add product category to store.
         * @param {Object} productCategory - The product category to add.
         */
        addProductCategory(productCategory) {
            this.productCategories = [
                ...this.productCategories,
                { ...productCategory }
            ];
        },

        /**
         * Remove product category from the backend API and store.
         * @param {Object} category - The product category to remove.
         * @returns {Promise} Promise object representing the API request.
         */
        removeProductCategoryAction(category) {
            return new Promise((resolve, reject) => {
                axios
                    .delete(`/product_categories/${category.id}/`)
                    .then(response => {
                        this.deleteProductCategory(category);
                        resolve(response);
                    })
                    .catch(error => {
                        console.error(
                            'Failed to remove product category due to: ',
                            error
                        );
                        reject(error);
                    });
            });
        },

        /**
         * Delete product category from store.
         * @param {Object} productCategory - The product category to delete.
         */
        deleteProductCategory(productCategory) {
            this.productCategories = this.productCategories.filter(
                category => category.id !== productCategory.id
            );
        },

        /**
         * Update product category in the backend API and store.
         * @param {Object} category - The product category to update.
         * @returns {Promise} Promise object representing the API request.
         */
        updateProductCategoryAction(category) {
            return new Promise((resolve, reject) => {
                axios
                    .put(`/product_categories/${category.id}/`, category)
                    .then(response => {
                        let productCategory = {
                            id: response.data.id,
                            name: response.data.name
                        };
                        this.patchProductCategory(productCategory);
                        resolve(response);
                    })
                    .catch(error => {
                        console.error(
                            'Failed to update product category due to: ',
                            error
                        );
                        reject(error);
                    });
            });
        },

        /**
         * Update product category in store.
         * @param {Object} productCategory - The product category to update.
         */
        patchProductCategory(productCategory) {
            this.productCategories = this.productCategories.map(category =>
                category.id === productCategory.id
                    ? { ...productCategory }
                    : category
            );
        },

        /**
         * Create product property in the backend API and store.
         * @param {Object} property - The product property to create.
         * @returns {Promise} Promise object representing the API request.
         */
        createProductPropertyAction(property) {
            return new Promise((resolve, reject) => {
                axios
                    .post(`/product_properties/`, property)
                    .then(response => {
                        let productProperty = {
                            id: response.data.id,
                            name: response.data.name,
                            product_category: response.data.product_category,
                            allowed_values: response.data.allowed_values
                        };
                        this.addProductProperty(productProperty);
                        resolve(response);
                    })
                    .catch(error => {
                        console.error(
                            'Failed to create product property due to: ',
                            error
                        );
                        reject(error);
                    });
            });
        },

        /**
         * Add product property to store.
         * @param {Object} productProperty - The product property to add.
         */
        addProductProperty(productProperty) {
            this.productProperties = [
                ...this.productProperties,
                { ...productProperty }
            ];
        },

        /**
         * Update product property in the backend API and store.
         * @param {Object} property - The product property to update.
         * @returns {Promise} Promise object representing the API request.
         */
        updateProductPropertyAction(property) {
            return new Promise((resolve, reject) => {
                axios
                    .put(`/product_properties/${property.id}/`, property)
                    .then(response => {
                        let productProperty = {
                            id: response.data.id,
                            name: response.data.name,
                            product_category: response.data.product_category,
                            allowed_values: response.data.allowed_values
                        };
                        this.patchProductProperty(productProperty);
                        resolve(response);
                    })
                    .catch(error => {
                        console.error(
                            'Failed to update product property due to: ',
                            error
                        );
                        reject(error);
                    });
            });
        },

        /**
         * Update product property in store.
         * @param {Object} productProperty - The product property to update.
         */
        patchProductProperty(productProperty) {
            this.productProperties = this.productProperties.map(property =>
                property.id === productProperty.id
                    ? { ...productProperty }
                    : property
            );
        },

        /**
         * Remove product property from the backend API and store.
         * @param {Object} property - The product property to remove.
         * @returns {Promise} Promise object representing the API request.
         */
        removeProductPropertyAction(property) {
            return new Promise((resolve, reject) => {
                axios
                    .delete(`/product_properties/${property.id}/`)
                    .then(response => {
                        this.deleteProductProperty(property);
                        resolve(response);
                    })
                    .catch(error => {
                        console.error(
                            'Failed to remove product property due to: ',
                            error
                        );
                        reject(error);
                    });
            });
        },

        /**
         * Remove product property from store.
         * @param {Object} productProperty - The product property to remove.
         */
        deleteProductProperty(productProperty) {
            this.productProperties = this.productProperties.filter(
                property => property.id !== productProperty.id
            );
        },

        /**
         * Formats a product object by extracting and restructuring its properties.
         * @param {Object} product - The product object to format.
         */
        formatAndSetProductAction(product) {
            const formattedProduct = this.formatProduct(product);
            this.setProduct(formattedProduct);
        },

        /**
         * Formats a product object by extracting and restructuring its properties.
         * @param {Object} product - The product object to format.
         * @param {number|string} product.id - The unique identifier of the product.
         * @param {string} product.facility - The facility associated with the product.
         * @param {string} product.name - The name of the product.
         * @param {string} product.code - The code of the product.
         * @param {number} product.product_category - The product category id.
         * @param {Array} product.properties - The properties of the product.
         * @param {string} product.description - The description of the product.
         * @param {string} product.unit_packing - The unit packing of the product.
         * @param {string} product.unit_measure - The unit measure of the product.
         * @returns {Object} The formatted product object.
         */
        formatProduct(product) {
            if (!product) {
                return null;
            }
            return {
                id: product.id,
                facility: product.facility,
                name: product.name,
                code: product.code,
                product_category: product.product_category || null,
                properties: product.properties || [],
                description: product.description || '',
                unit_packing: product.unit_packing || '',
                unit_measure: product.unit_measure || ''
            };
        }
    }
});
